import { useState } from "react";

import { useStoreCurrentSetupPageMutation } from "@shared/data/mutations/StoreCurrentSetupPageMutation/StoreCurrentSetupPage.mutation";

import { CanadaOAuthOnboardingFlow } from "../CanadaOAuthOnboardingFlow";

import { OAuthAccountTypeStep } from "./OAuthAccountTypeStep";
import { OAuthAuthorizationStep } from "./OAuthAuthorizationStep";
import { OAuthBankingStep } from "./OAuthBankingStep";
import { OAuthCertificationStep } from "./OAuthCertificationStep";
import { OAuthExternalApplicationStep } from "./OAuthExternalApplicationStep/OAuthExternalApplicationStep";
import { OAuthLandingStep } from "./OAuthLandingStep";
import { OAuthOnboardingWrapper } from "./OAuthOnboardingWrapper";
import { OAuthSignInStep } from "./OAuthSignInStep";
import { OAuthSignUpStep } from "./OAuthSignUpStep";
import { OAuthStep } from "./oauthStep";
import { EXTERNAL_APPLICATION_STRING } from "./types";

interface Props {
  applicationName: string;
  applicationLogo: string;
  clientId: string;
  csuid: string;
  redirectUri: string;
  responseType: string;
  scopes: string;
  state: string;
  applicationAccessLevel: string;
  ssoErrors?: string;
  ssoOrigin: string;
  currentCountry: string;
}

type OauthStoreCurrentSetupPage = "student_identification" | "certificate" | "complete";

const OAuthOnboardingFlow = ({ currentCountry, ...props }: Props) => {
  const {
    applicationLogo,
    clientId,
    csuid,
    redirectUri,
    responseType,
    scopes,
    state,
    applicationAccessLevel,
    ssoErrors,
    ssoOrigin,
  } = props;

  const [ssoOriginState, setSsoOriginState] = useState(ssoOrigin);
  const [ssoErrorMessage, setSsoErrorMessage] = useState(ssoErrors);
  const isExternalApp = applicationAccessLevel === EXTERNAL_APPLICATION_STRING;
  const [currentStep, setCurrentStep] = useState<OAuthStep>(
    isExternalApp ? OAuthStep.EXTERNAL_APPLICATION : OAuthStep.LANDING_PAGE
  );
  const [isFromSignIn, setIsFromSignIn] = useState(false);
  const [updateCurrentSetupPage] = useStoreCurrentSetupPageMutation();
  const target = history.state?.target;

  const handleSignInComplete = () => {
    if (isExternalApp) {
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
      setIsFromSignIn(true);
    }
  };

  const handleOnboardingComplete = () => {
    if (isExternalApp) {
      setSsoOriginState(null);
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
    }
  };

  const updateStoreCurrentSetupPage = async (
    step: OauthStoreCurrentSetupPage,
    forProfit?: boolean
  ) => {
    await updateCurrentSetupPage({
      variables: {
        input: {
          attributes: {
            skipToStep: step,
            apiClientId: clientId,
            forProfit: forProfit || false,
          },
        },
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case OAuthStep.SIGN_IN:
        return (
          <OAuthSignInStep
            onSignInComplete={handleSignInComplete}
            ssoErrors={ssoErrorMessage}
            setSsoErrorMessage={setSsoErrorMessage}
            onUpdateCurrentStep={setCurrentStep}
          />
        );
      case OAuthStep.SIGN_UP:
        return (
          <OAuthSignUpStep
            setSsoErrorMessage={setSsoErrorMessage}
            clientId={clientId}
            onUpdateCurrentStep={setCurrentStep}
            csuid={csuid}
            ssoErrors={ssoErrorMessage}
          />
        );
      case OAuthStep.EXTERNAL_APPLICATION:
        return (
          <OAuthExternalApplicationStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
            redirectUri={redirectUri}
          />
        );
      case OAuthStep.CERTIFICATION:
        return (
          <OAuthCertificationStep
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateCurrentStep={setCurrentStep}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.ACCOUNT_TYPE:
        return (
          <OAuthAccountTypeStep
            onUpdateCurrentStep={setCurrentStep}
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.PAYMENT:
        return (
          <OAuthBankingStep
            onUpdateCurrentStep={setCurrentStep}
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.AUTHORIZATION:
        return (
          <OAuthAuthorizationStep
            clientId={clientId}
            redirectUri={redirectUri}
            responseType={responseType}
            scopes={scopes}
            state={state}
            isFromSignIn={isFromSignIn}
          />
        );
      case OAuthStep.LANDING_PAGE:
        return (
          <OAuthLandingStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
          />
        );
    }
  };

  if (currentCountry === "Canada") return <CanadaOAuthOnboardingFlow {...props} />;

  return (
    <div data-testid="us-oauth">
      <OAuthOnboardingWrapper
        onUpdateCurrentStep={setCurrentStep}
        currentStep={currentStep}
        applicationLogo={applicationLogo}
      >
        {renderCurrentStep()}
      </OAuthOnboardingWrapper>
    </div>
  );
};

export { OAuthOnboardingFlow };
